import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { projectsApi } from '../../api/projects';
import { seriesGetIdeasApi } from '../../api/seriesGetIdeas';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { BOOK_LINE_LABEL, BOOK_LINE_PLACEHOLDER } from '../../utils/seriesSharedBooks';
import {
  persistAcceptedBookIdea,
  loadMakeCardsSources,
  selectedIds,
} from '../../utils/seriesOffers';
import { seriesDiscoveryPath } from '../../utils/seriesLanding';
import {
  DISCOVERY_POINTER,
  GATE_MISSING_THROUGH_LINE,
  GATE_NEEDS_ENDING,
  GATE_READY,
  GATE_UNKNOWN_SHAPE,
  GET_IDEAS_ACCEPT,
  GET_IDEAS_ASK_ENDING,
  GET_IDEAS_CHECKLIST_LEDE,
  GET_IDEAS_DISCARD,
  GET_IDEAS_EDIT,
  GET_IDEAS_ENDING_LEDE,
  GET_IDEAS_LABEL,
  GET_IDEAS_REVIEW_LEDE,
  evaluateGetIdeasGate,
  sitAfterNote,
} from '../../utils/seriesGetIdeas';
import SeriesShapeChooser from './SeriesShapeChooser';
import './SeriesGetIdeasPanel.css';

/**
 * Get Book Ideas modal on the Series Timeline (#770 / #778).
 * Writer click only. Not Make cards. Overlays the board.
 * Accept writes a Timeline idea pin through persistAcceptedBookIdea.
 * Never starts a book. The pin stays on the line.
 * Checked talks and notes can satisfy what holds the books
 * together (#794 / #800). Existing uploaded series note files
 * on the project appear in this picker and count, not only
 * Discovery chat notes (#796 / #800). Not a required theme.
 */
export default function SeriesGetIdeasPanel({
  projectId,
  project,
  onClose,
  onProjectPatch,
  onAcceptedPin,
}) {
  const [facts, setFacts] = useState(project);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [talks, setTalks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [checked, setChecked] = useState({});
  const [ideas, setIdeas] = useState([]);
  const [endings, setEndings] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [done, setDone] = useState({});
  const [editing, setEditing] = useState({});
  const [ownEnding, setOwnEnding] = useState('');
  const [askWriterEnding, setAskWriterEnding] = useState(false);
  const [serverCopy, setServerCopy] = useState('');
  const [loading, setLoading] = useState(true);
  const [proposing, setProposing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [asked, setAsked] = useState(false);

  const gate = evaluateGetIdeasGate(facts);
  const showDiscoveryHold = !loading && asked && gate.pointToDiscovery;
  const showChecklist = !loading && !showDiscoveryHold && (
    gate.gate === GATE_READY
    || gate.gate === GATE_NEEDS_ENDING
    || gate.gate === GATE_UNKNOWN_SHAPE
    || gate.gate === GATE_MISSING_THROUGH_LINE
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectId) return;
      setLoading(true);
      try {
        const nextProject = await projectsApi.get(projectId);
        if (!cancelled && nextProject) {
          setFacts(nextProject);
          onProjectPatch?.(nextProject);
        }
        const checklist = await loadMakeCardsSources(
          projectId,
          nextProject?.discovery_session_id || project?.discovery_session_id,
        );
        if (cancelled) return;
        setTalks(checklist.talks);
        setNotes(checklist.notes);
        const nextChecked = {};
        checklist.talks.forEach((item) => { nextChecked[item.id] = true; });
        checklist.notes.forEach((item) => { nextChecked[item.id] = false; });
        setChecked(nextChecked);
        setError(
          checklist.error && checklist.talks.length === 0 && checklist.notes.length === 0
            ? (checklist.error.message || 'Could not open Get Book Ideas.')
            : null,
        );
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not open Get Book Ideas.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionIds = useMemo(() => selectedIds(checked, talks), [checked, talks]);
  const batchIds = useMemo(() => selectedIds(checked, notes), [checked, notes]);
  const discoveryHref = seriesDiscoveryPath(facts || project);

  const toggle = (id) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const applyResult = (result, nextFacts) => {
    const patched = { ...(nextFacts || facts || {}) };
    if (result?.ends_or_continues) patched.ends_or_continues = result.ends_or_continues;
    if (result?.through_line_thing) patched.through_line_thing = result.through_line_thing;
    setFacts(patched);
    onProjectPatch?.(patched);
    const nextGate = result?.gate || evaluateGetIdeasGate(patched).gate;
    setServerCopy(result?.copy || '');
    setAskWriterEnding(Boolean(result?.ask_writer_ending) || nextGate === GATE_NEEDS_ENDING);
    setIdeas(result?.ideas || []);
    setEndings(result?.endings || []);
    const nextDrafts = {};
    (result?.ideas || []).forEach((row) => {
      nextDrafts[row.id] = { title: row.title, proposition: row.proposition };
    });
    setDrafts(nextDrafts);
    setDone({});
    setEditing({});
  };

  const propose = async () => {
    const ok = await askPaid('get_ideas');
    if (!ok) return;
    setProposing(true);
    try {
      const result = await seriesGetIdeasApi.propose(projectId, { sessionIds, batchIds });
      setAsked(true);
      applyResult(result);
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not get ideas from those chats and notes.');
    } finally {
      setProposing(false);
    }
  };

  const acceptIdea = async (idea) => {
    const draft = drafts[idea.id] || idea;
    const title = (draft.title || '').trim();
    const proposition = (draft.proposition || '').trim();
    if (!title || !proposition) {
      setError('Give this offer a name and a line before you accept it.');
      return;
    }
    setSavingId(idea.id);
    try {
      const card = await persistAcceptedBookIdea(projectId, { title, statement: proposition });
      onAcceptedPin?.(card);
      setDone((prev) => ({ ...prev, [idea.id]: 'accepted' }));
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not accept this book idea.');
    } finally {
      setSavingId(null);
    }
  };

  const discardIdea = (id) => {
    setDone((prev) => ({ ...prev, [id]: 'discarded' }));
  };

  const rememberShape = async (shape) => {
    const remembered = String(shape || '').trim();
    if (!remembered) {
      setError('Name how this series is built before we remember it.');
      return;
    }
    setSavingId('shape');
    try {
      const saved = await seriesGetIdeasApi.rememberShape(projectId, remembered);
      const nextFacts = {
        ...(facts || {}),
        ends_or_continues: saved.ends_or_continues,
        through_line_thing: saved.through_line_thing ?? facts?.through_line_thing,
      };
      setFacts(nextFacts);
      onProjectPatch?.(nextFacts);
      setServerCopy('');
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not remember how this series is built.');
    } finally {
      setSavingId(null);
    }
  };

  const rememberEnding = async (statement) => {
    const ending = (statement || '').trim();
    if (!ending) {
      setError('Name the ending before we remember it.');
      return;
    }
    setSavingId('ending');
    try {
      const saved = await seriesGetIdeasApi.rememberEnding(projectId, ending);
      const nextFacts = { ...(facts || {}), series_ending: saved.series_ending };
      setFacts(nextFacts);
      onProjectPatch?.(nextFacts);
      setEndings([]);
      setAskWriterEnding(false);
      setOwnEnding('');
      setServerCopy('');
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not remember that ending.');
    } finally {
      setSavingId(null);
    }
  };

  const renderSourceList = (items, heading) => (
    <section className="sgi-section">
      <h3>{heading}</h3>
      {items.length === 0 && <p className="sgi-empty">None in this project yet.</p>}
      <ul className="sgi-list">
        {items.map((item) => (
          <li key={item.id}>
            <label className="sgi-check">
              <input
                type="checkbox"
                checked={Boolean(checked[item.id])}
                onChange={() => toggle(item.id)}
              />
              <span>
                <strong>{item.label}</strong>
                <em>{item.detail}</em>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );

  return createPortal(
    <div
      className="sgi-overlay"
      data-testid="get-book-ideas-overlay"
      onMouseDown={onClose}
    >
      {paidDialog}
      <section
        className="sgi-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sgi-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
      <div className="sgi-modal__bar">
        <h2 id="sgi-title" className="sgi-panel__title">{GET_IDEAS_LABEL}</h2>
        <button type="button" className="sgi-close" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="sgi-modal__body">
      <p className="sgi-lede">
        These are ideas for books. If you take one, it lands on the Timeline.
        It does not start a book.
      </p>
      {error && <p className="sgi-error" role="alert">{error}</p>}
      {loading && <p className="sgi-lede">Loading…</p>}

      {showDiscoveryHold && (
        <p className="sgi-gate">
          {serverCopy || gate.copy}
          {' '}
          <Link to={discoveryHref}>{DISCOVERY_POINTER}</Link>
        </p>
      )}

      {!loading && gate.gate === GATE_UNKNOWN_SHAPE && (
        <SeriesShapeChooser
          flush
          remembered={facts?.ends_or_continues}
          onChoose={rememberShape}
          saving={savingId === 'shape'}
        />
      )}

      {showChecklist && ideas.length === 0 && endings.length === 0 && (
        <>
          {gate.gate === GATE_NEEDS_ENDING && (
            <p className="sgi-gate">{serverCopy || GET_IDEAS_ENDING_LEDE}</p>
          )}
          <p className="sgi-lede">{GET_IDEAS_CHECKLIST_LEDE}</p>
          {renderSourceList(talks, 'Series chats')}
          {renderSourceList(notes, 'Uploaded files')}
          <button
            type="button"
            className="sgi-primary"
            onClick={propose}
            disabled={proposing}
          >
            {proposing
              ? 'Looking…'
              : (gate.gate === GATE_NEEDS_ENDING ? 'Look for possible endings' : GET_IDEAS_LABEL)}
          </button>
        </>
      )}

      {endings.length > 0 && (
        <div className="sgi-offers">
          <p className="sgi-gate">{serverCopy || GET_IDEAS_ENDING_LEDE}</p>
          <ul className="sgi-cards">
            {endings.map((ending) => (
              <li key={ending.id} className="sgi-card">
                <p>{ending.statement}</p>
                <button
                  type="button"
                  className="sgi-primary"
                  onClick={() => rememberEnding(ending.statement)}
                  disabled={savingId === 'ending'}
                >
                  Use this ending
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(askWriterEnding || gate.gate === GATE_NEEDS_ENDING) && ideas.length === 0 && (
        <label className="sgi-field">
          <span>Or write the ending</span>
          <textarea
            rows={2}
            value={ownEnding}
            onChange={(e) => setOwnEnding(e.target.value)}
            placeholder="How this series ends — one short line."
          />
          <button
            type="button"
            className="sgi-primary"
            onClick={() => rememberEnding(ownEnding)}
            disabled={savingId === 'ending'}
          >
            Use this ending
          </button>
        </label>
      )}

      {askWriterEnding && !endings.length && serverCopy === GET_IDEAS_ASK_ENDING && (
        <p className="sgi-gate">{GET_IDEAS_ASK_ENDING}</p>
      )}

      {ideas.length > 0 && (
        <div className="sgi-offers">
          <p className="sgi-lede">{GET_IDEAS_REVIEW_LEDE}</p>
          <ul className="sgi-cards">
            {ideas.map((idea) => {
              const status = done[idea.id];
              const draft = drafts[idea.id] || idea;
              const isEditing = Boolean(editing[idea.id]);
              if (status) {
                return (
                  <li key={idea.id} className="sgi-card sgi-card--done">
                    <p>{status === 'accepted' ? 'Accepted.' : 'Discarded.'} {idea.title}</p>
                  </li>
                );
              }
              return (
                <li key={idea.id} className="sgi-card">
                  {isEditing ? (
                    <>
                      <label className="sgi-field">
                        <span>Title</span>
                        <input
                          value={draft.title}
                          onChange={(e) => setDrafts((prev) => ({
                            ...prev,
                            [idea.id]: { ...draft, title: e.target.value },
                          }))}
                        />
                      </label>
                      <label className="sgi-field">
                        <span>{BOOK_LINE_LABEL}</span>
                        <input
                          value={draft.proposition}
                          onChange={(e) => setDrafts((prev) => ({
                            ...prev,
                            [idea.id]: { ...draft, proposition: e.target.value },
                          }))}
                          placeholder={BOOK_LINE_PLACEHOLDER}
                        />
                      </label>
                    </>
                  ) : (
                    <>
                      <p className="sgi-card__title">{draft.title}</p>
                      <p className="sgi-card__line">{draft.proposition}</p>
                    </>
                  )}
                  {idea.could_sit_after && (
                    <p className="sgi-hint">{sitAfterNote(idea.could_sit_after)}</p>
                  )}
                  <div className="sgi-actions">
                    <button
                      type="button"
                      className="sgi-primary"
                      onClick={() => acceptIdea(idea)}
                      disabled={savingId === idea.id}
                    >
                      {GET_IDEAS_ACCEPT}
                    </button>
                    <button
                      type="button"
                      className="sgi-edit"
                      aria-pressed={isEditing}
                      onClick={() => setEditing((prev) => ({ ...prev, [idea.id]: true }))}
                      disabled={savingId === idea.id}
                    >
                      {GET_IDEAS_EDIT}
                    </button>
                    <button
                      type="button"
                      className="sgi-skip"
                      onClick={() => discardIdea(idea.id)}
                      disabled={savingId === idea.id}
                    >
                      {GET_IDEAS_DISCARD}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      </div>
      </section>
    </div>,
    document.body,
  );
}

export { GET_IDEAS_LABEL };
